import { useCallback, type MouseEventHandler } from 'react'
import { useIntl } from 'react-intl'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useNavigateTo from 'hooks/useNavigateTo'
import { CHAT_TRIP_PLANNER_NEW_PATH } from 'tripPlanner/config'
import { appendContextTag } from 'tripPlanner/utils/contextTag'
import { buildStagedChatPromptQuery } from 'tripPlanner/utils/stagedChatPrompt'
import {
  thereNoPlaceLikeHomePopupVariantConfig,
  thereNoPlaceLikeHomePopupMessages,
  type ThereNoPlaceLikeHomePopupActionId,
  type ThereNoPlaceLikeHomePopupMarket,
  type ThereNoPlaceLikeHomePopupVariant,
} from './thereNoPlaceLikeHomePopupConfig'
import {
  trackThereNoPlaceLikeHomePopupActionClicked,
  trackThereNoPlaceLikeHomePopupCloseClicked,
  trackThereNoPlaceLikeHomePopupRemindMeNextTimeClicked,
} from './thereNoPlaceLikeHomePopupAnalytics'
import {
  markThereNoPlaceLikeHomePopupAsSeen,
  snoozeThereNoPlaceLikeHomePopupForSession,
} from './thereNoPlaceLikeHomePopupStorage'

export type ThereNoPlaceLikeHomePopupActionClickHandler = MouseEventHandler<HTMLElement>

function isThereNoPlaceLikeHomePopupActionId(actionId: string | undefined): actionId is ThereNoPlaceLikeHomePopupActionId {
  return (
    actionId === 'firstPrompt' ||
    actionId === 'secondPrompt' ||
    actionId === 'thirdPrompt' ||
    actionId === 'tryAiTripPlanner' ||
    actionId === 'exploreNswEscapes' ||
    actionId === 'exploreVicEscapes'
  )
}

function useThereNoPlaceLikeHomePopupActions(market: ThereNoPlaceLikeHomePopupMarket, variant: ThereNoPlaceLikeHomePopupVariant) {
  const modalElement = useModalElementContext()
  const intl = useIntl()
  const navigateTo = useNavigateTo()
  const variantConfig = thereNoPlaceLikeHomePopupVariantConfig[market][variant]

  const handleClose = useCallback(() => {
    trackThereNoPlaceLikeHomePopupCloseClicked(market, variant, intl.formatMessage(thereNoPlaceLikeHomePopupMessages.close))
    markThereNoPlaceLikeHomePopupAsSeen()
    modalElement?.resolve()
  }, [intl, market, modalElement, variant])

  const handleRemindMeNextTimeClick = useCallback(() => {
    trackThereNoPlaceLikeHomePopupRemindMeNextTimeClicked(
      market,
      variant,
      intl.formatMessage(thereNoPlaceLikeHomePopupMessages.remindMeNextTime),
    )
    snoozeThereNoPlaceLikeHomePopupForSession()
    modalElement?.resolve()
  }, [intl, market, modalElement, variant])

  const handleActionClick = useCallback<ThereNoPlaceLikeHomePopupActionClickHandler>((event) => {
    const actionId = event.currentTarget.dataset.thereNoPlaceLikeHomePopupActionId

    if (!isThereNoPlaceLikeHomePopupActionId(actionId)) {
      return
    }

    const actionDestination = variantConfig.actions[actionId]
    if (!actionDestination) {
      return
    }

    const promptActionConfig = variantConfig.prompts.find(prompt => prompt.actionId === actionId)
    const actionConfig = promptActionConfig ?? (
      variantConfig.primaryAction.actionId === actionId ? variantConfig.primaryAction : undefined
    )
    const buttonText = actionConfig ? intl.formatMessage(actionConfig.message) : actionId

    trackThereNoPlaceLikeHomePopupActionClicked(market, variant, actionDestination, buttonText)
    markThereNoPlaceLikeHomePopupAsSeen()

    if (actionDestination.type === 'chatPrompt') {
      let messageWithContext: string | undefined

      if (actionConfig) {
        const prompt = intl.formatMessage(actionConfig.promptMessage ?? actionConfig.message)
        messageWithContext = prompt

        if (actionConfig.contextMessage && actionConfig.contextLabel) {
          messageWithContext = appendContextTag(prompt, actionConfig.contextMessage, actionConfig.contextLabel)
        }
      }

      const search = messageWithContext ? buildStagedChatPromptQuery(messageWithContext) : undefined

      navigateTo(CHAT_TRIP_PLANNER_NEW_PATH, search)
    } else {
      navigateTo(actionDestination.path, actionDestination.search)
    }

    modalElement?.resolve()
  }, [intl, market, modalElement, navigateTo, variant, variantConfig])

  return {
    handleActionClick,
    handleClose,
    handleRemindMeNextTimeClick,
  }
}

export default useThereNoPlaceLikeHomePopupActions
